import { useFilter } from "@ttc3k/trekker";
import {
  EnumOwnerType,
  EnumTagEntityTypes,
  EnumTagGroupKey,
  EnumTagGroupType,
  useTagManyQuery,
} from "gql/generated";
import { useMemo } from "react";
import { useTagGroupContext } from "shared/contextProviders/tagGroups";
import { getMultilingualString } from "shared/utils";
import _ from "lodash";

/**
 * Fetches organization-specific operator tags (Custom Tags / BackOffice)
 * for use in Widget Settings tiered sort order dropdown.
 */
export const useOperatorTagsForSettings = (
  organizationId: string | undefined,
  entityType: (typeof EnumTagEntityTypes)[keyof typeof EnumTagEntityTypes] = EnumTagEntityTypes.Operator,
  tagGroupKey: (typeof EnumTagGroupKey)[keyof typeof EnumTagGroupKey] = EnumTagGroupKey.BackOffice,
) => {
  const { getTagGroup } = useTagGroupContext();

  const tagGroupData = useMemo(
    () =>
      getTagGroup({
        key: tagGroupKey,
        type: EnumTagGroupType.OrganizationSpecific,
      }),
    [getTagGroup, tagGroupKey],
  );

  const { data: tagData } = useTagManyQuery({
    variables: {
      filter: {
        tagGroup: tagGroupData?._id ? [tagGroupData._id] : null,
        owner: {
          entityId: organizationId,
          type: EnumOwnerType.Organization,
        },
        entityTypes: [entityType],
      },
      limit: 10000,
    },
    skip: !organizationId || !tagGroupData?._id,
  });

  const tagOptions = useMemo(
    () =>
      tagData?.tagMany
        ? _.sortBy(
            tagData.tagMany.map(tag => ({
              value: String(tag._id),
              label: getMultilingualString(tag.name),
            })),
            "label",
          )
        : [],
    [tagData?.tagMany],
  );

  const { contains } = useFilter({ sensitivity: "base" });

  // useCollection expects (itemText, filterText, item) => boolean
  const filterPredicate = useMemo(
    () => (itemText: string, filterText: string) =>
      contains(itemText, filterText),
    [contains],
  );

  return {
    tagOptions,
    filterPredicate,
    loading: !tagData && !!organizationId,
  };
};
